import type { ChangeEvent, ReactNode } from 'react'

import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TextFieldProps {
    id: string
    label: string
    value: string
    onChange: (value: string) => void
    type?: string
    error?: string
    hint?: string
    autoComplete?: string
    required?: boolean
    placeholder?: string
    inputMode?: 'text' | 'email' | 'tel' | 'numeric'
    className?: string
}

export const TextField = ({
    id,
    label,
    value,
    onChange,
    type = 'text',
    error,
    hint,
    autoComplete,
    required,
    placeholder,
    inputMode,
    className
}: TextFieldProps): ReactNode => (
    <div className={className}>
        <label htmlFor={id} className='label-mono text-star-3'>
            {label}
            {!required && (
                <span className='ml-2 normal-case tracking-normal text-star-3/80'>
                    (optional)
                </span>
            )}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
            autoComplete={autoComplete}
            required={required}
            placeholder={placeholder}
            inputMode={inputMode}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            className='field mt-1'
        />
        {error ? (
            <p id={`${id}-error`} className='mt-2 text-sm text-signal'>
                {error}
            </p>
        ) : hint ? (
            <p id={`${id}-hint`} className='mt-2 text-sm text-star-3'>
                {hint}
            </p>
        ) : null}
    </div>
)

interface TextAreaProps {
    id: string
    label: string
    value: string
    onChange: (value: string) => void
    error?: string
    hint?: string
    required?: boolean
    rows?: number
    maxLength?: number
    className?: string
}

export const TextArea = ({ id, label, value, onChange, error, hint, required, rows = 4, maxLength, className }: TextAreaProps): ReactNode => (
    <div className={className}>
        <label htmlFor={id} className='label-mono text-star-3'>
            {label}
            {!required && (
                <span className='ml-2 normal-case tracking-normal text-star-3/80'>
                    (optional)
                </span>
            )}
        </label>
        <textarea
            id={id}
            value={value}
            rows={rows}
            maxLength={maxLength}
            onChange={event => onChange(event.target.value)}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            className='field mt-1 resize-y leading-relaxed'
        />
        {error ? (
            <p id={`${id}-error`} className='mt-2 text-sm text-signal'>
                {error}
            </p>
        ) : hint ? (
            <p id={`${id}-hint`} className='mt-2 text-sm text-star-3'>
                {hint}
            </p>
        ) : null}
    </div>
)

interface CheckboxProps {
    id: string
    checked: boolean
    onChange: (checked: boolean) => void
    label: ReactNode
    description?: ReactNode
    className?: string
}

export const Checkbox = ({ id, checked, onChange, label, description, className }: CheckboxProps): ReactNode => (
    <div className={cn('flex items-start gap-3', className)}>
        <span className='relative mt-1 flex size-4 shrink-0 items-center justify-center'>
            <input
                id={id}
                type='checkbox'
                checked={checked}
                onChange={event => onChange(event.target.checked)}
                className='peer absolute inset-0 size-4 appearance-none rounded-[3px] border border-hair-2 bg-transparent transition-colors checked:border-brass checked:bg-brass focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-brass'
            />
            <svg viewBox='0 0 12 12' aria-hidden className='pointer-events-none relative size-2.5 opacity-0 peer-checked:opacity-100'>
                <path d='M2 6.2 L4.8 9 L10 3' fill='none' stroke='var(--c-ink)' strokeWidth='1.6' />
            </svg>
        </span>
        <label htmlFor={id} className='text-star-2'>
            <span className='text-star'>
                {label}
            </span>
            {description && (
                <span className='mt-0.5 block text-sm text-star-3'>
                    {description}
                </span>
            )}
        </label>
    </div>
)

interface StepperProps {
    value: number
    onChange: (value: number) => void
    min?: number
    max?: number
    label: string
}

export const Stepper = ({ value, onChange, min = 0, max = 12, label }: StepperProps): ReactNode => (
    <div className='flex items-center gap-3' role='group' aria-label={label}>
        <button
            type='button'
            onClick={() => onChange(Math.max(min, value - 1))}
            disabled={value <= min}
            aria-label={`Fewer ${label.toLowerCase()} tickets`}
            className='inline-flex size-9 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass disabled:opacity-35'
        >
            <Minus aria-hidden className='size-3.5' strokeWidth={1.25} />
        </button>
        <span className='data-mono w-6 text-center text-lg text-star' aria-live='polite'>
            {value}
        </span>
        <button
            type='button'
            onClick={() => onChange(Math.min(max, value + 1))}
            disabled={value >= max}
            aria-label={`More ${label.toLowerCase()} tickets`}
            className='inline-flex size-9 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass disabled:opacity-35'
        >
            <Plus aria-hidden className='size-3.5' strokeWidth={1.25} />
        </button>
    </div>
)

interface ChoiceProps {
    name: string
    value: string
    checked: boolean
    onChange: (value: string) => void
    title: ReactNode
    description?: ReactNode
    aside?: ReactNode
    disabled?: boolean
}

export const Choice = ({ name, value, checked, onChange, title, description, aside, disabled }: ChoiceProps): ReactNode => (
    <label
        className={cn(
            'flex cursor-pointer items-start gap-4 border px-5 py-4 transition-colors duration-500 has-[:focus-visible]:outline has-[:focus-visible]:outline-1 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brass',
            checked ? 'border-brass bg-brass/[0.06]' : 'border-hair hover:border-hair-2',
            disabled && 'cursor-not-allowed opacity-50'
        )}
    >
        <input
            type='radio'
            name={name}
            value={value}
            checked={checked}
            disabled={disabled}
            onChange={() => onChange(value)}
            className='peer sr-only'
        />
        <span aria-hidden className={cn('mt-1.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border', checked ? 'border-brass' : 'border-hair-2')}>
            {checked && <span className='size-1.5 rounded-full bg-brass' />}
        </span>
        <span className='flex-1'>
            <span className='flex items-baseline justify-between gap-4'>
                <span className='font-heading text-xl text-star'>
                    {title}
                </span>
                {aside && (
                    <span className='data-mono text-star-2'>
                        {aside}
                    </span>
                )}
            </span>
            {description && (
                <span className='mt-1 block text-sm text-star-3'>
                    {description}
                </span>
            )}
        </span>
    </label>
)

export const Notice = ({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success', children: ReactNode }): ReactNode => (
    <div
        role={tone === 'error' ? 'alert' : 'status'}
        className={cn(
            'border-l px-4 py-3 text-sm',
            tone === 'error' && 'border-signal bg-signal/[0.08] text-star',
            tone === 'success' && 'border-brass bg-brass/[0.08] text-star',
            tone === 'info' && 'border-hair-2 bg-ink-2 text-star-2'
        )}
    >
        {children}
    </div>
)
